/* Direction-aware sticky sidebar, without scroll jitter.
 *
 * For a sidebar taller than the viewport, plain `position: sticky` can only
 * pin one edge. This gives the behaviour readers expect — scrolling down the
 * sidebar travels with the page until its bottom edge meets the viewport
 * bottom and stays there; scrolling up it travels until its top edge meets
 * the viewport top and stays there — while leaving the actual pinning to
 * native `position: sticky`, so it moves on the compositor thread with no
 * per-frame JavaScript and no lag.
 *
 * The script only acts at transitions:
 *   - when the scroll direction flips, the pinned sidebar is released in
 *     place (position: relative at its current offset) and scrolls naturally;
 *   - when its leading edge then reaches the viewport line, it is re-pinned
 *     exactly where it is: sticky `top` from the container start when
 *     scrolling up, sticky `bottom` from the container end (margin-top)
 *     when scrolling down. Both hand-overs are position-identical.
 * A sidebar short enough to fit is simply sticky at the top. It never leaves
 * its container, and the container growing (infinite scroll appending posts)
 * does not move it.
 *
 * Markup contract: an element with data-sticky-sidebar whose parent is the
 * column stretched to the row height (.gh-sidebar or .gh-rail), laid out as
 * a flex column so margin-top: auto can place the element at its end. Active
 * from 1200px up, where the sidebar sits beside the feed.
 */
(function () {
    const MEDIA = '(min-width: 1200px)';
    const OFFSET = 21;     // = --grid-gap / 2, the gap the theme's own sticky used
    const TOLERANCE = 60;  // px past the line we accept when pinning in place (a
                           // bigger overshoot, from a fast flick, snaps to the line)
    const FREE = {mode: 'free', flow: 0, inset: 0, direction: 'down'};

    // Pure state machine. state: {mode, edge, inset, flow, direction}
    //   free:   position relative, top = flow (offset within the container)
    //   pinned: position sticky at the container start (edge 'top',
    //           top = inset) or at the container end (edge 'bottom',
    //           margin-top: auto in the flex column, bottom = inset). Sticky
    //           can only hold an element whose flow position lies beyond the
    //           viewport on the pinned side, hence the two placements. (An
    //           explicit margin would not do: sticky keeps the *margin box*
    //           inside the container, leaving no room to move.)
    // m: viewport-relative geometry {innerTop, innerHeight, containerTop,
    //   containerHeight, viewportHeight, offset, tolerance, direction}.
    // Returns the next state, or null when nothing needs to change.
    function next(state, m) {
        const maxFlow = Math.max(0, m.containerHeight - m.innerHeight);
        const flow = Math.min(maxFlow, Math.max(0, m.innerTop - m.containerTop));
        const fits = m.innerHeight <= m.viewportHeight - 2 * m.offset;

        if (fits) {
            if (state.mode === 'pinned' && state.edge === 'top' && state.inset === m.offset && state.flow === 0) {
                return null;
            }
            return {mode: 'pinned', edge: 'top', inset: m.offset, flow: 0, direction: m.direction};
        }

        if (state.mode === 'pinned') {
            if (state.direction === m.direction) {
                return null;
            }
            return {mode: 'free', flow: flow, inset: 0, direction: m.direction};
        }

        if (m.direction === 'down') {
            const bottom = m.innerTop + m.innerHeight;
            if (bottom <= m.viewportHeight - m.offset) {
                const inset = m.viewportHeight - bottom;
                return {mode: 'pinned', edge: 'bottom', inset: inset - m.offset <= m.tolerance ? inset : m.offset, flow: 0, direction: 'down'};
            }
        } else if (m.innerTop >= m.offset) {
            return {mode: 'pinned', edge: 'top', inset: m.innerTop - m.offset <= m.tolerance ? m.innerTop : m.offset, flow: 0, direction: 'up'};
        }

        if (state.direction !== m.direction) {
            return {mode: 'free', flow: state.flow, inset: 0, direction: m.direction};
        }
        return null;
    }

    function attach(inner) {
        const container = inner.parentElement;
        const media = window.matchMedia(MEDIA);
        let state = FREE;
        let lastScrollY = window.scrollY;

        function measure(direction) {
            return {
                innerTop: inner.getBoundingClientRect().top,
                innerHeight: inner.offsetHeight,
                containerTop: container.getBoundingClientRect().top,
                containerHeight: container.offsetHeight,
                viewportHeight: window.innerHeight,
                offset: OFFSET,
                tolerance: TOLERANCE,
                direction: direction
            };
        }

        function apply(s) {
            state = s;
            if (s.mode === 'pinned') {
                inner.style.position = 'sticky';
                inner.style.marginTop = s.edge === 'bottom' ? 'auto' : '';
                inner.style.top = s.edge === 'top' ? s.inset + 'px' : '';
                inner.style.bottom = s.edge === 'bottom' ? s.inset + 'px' : '';
            } else {
                inner.style.position = '';
                inner.style.marginTop = '';
                inner.style.top = s.flow ? s.flow + 'px' : '';
                inner.style.bottom = '';
            }
        }

        function clear() {
            state = FREE;
            inner.style.position = '';
            inner.style.top = '';
            inner.style.bottom = '';
            inner.style.marginTop = '';
        }

        function onScroll() {
            const y = window.scrollY;
            const direction = y > lastScrollY ? 'down' : (y < lastScrollY ? 'up' : state.direction);
            lastScrollY = y;
            if (!media.matches) {
                return;
            }
            const s = next(state, measure(direction));
            if (s) {
                apply(s);
            }
        }

        // Let go in place (keeps the current position) so the next scroll
        // re-evaluates against fresh sizes.
        function release() {
            if (!media.matches) {
                clear();
                return;
            }
            const m = measure(state.direction);
            const maxFlow = Math.max(0, m.containerHeight - m.innerHeight);
            apply({mode: 'free', flow: Math.min(maxFlow, Math.max(0, m.innerTop - m.containerTop)), inset: 0, direction: state.direction});
        }

        // The column changed height (infinite scroll appended posts): a
        // pinned sidebar is unaffected (margin-top: auto follows the new
        // container end); a free one only needs re-clamping.
        function onContainerResize() {
            if (media.matches && state.mode === 'free') {
                release();
            }
        }

        window.addEventListener('scroll', onScroll, {passive: true});
        window.addEventListener('resize', release);
        if (typeof media.addEventListener === 'function') {
            media.addEventListener('change', release);
        }
        if (window.ResizeObserver) {
            new ResizeObserver(release).observe(inner);
            new ResizeObserver(onContainerResize).observe(container);
        }
    }

    function init() {
        Array.prototype.forEach.call(document.querySelectorAll('[data-sticky-sidebar]'), attach);
    }

    window.htfpStickySidebar = {next: next, init: init};
    init();
})();
